import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../../../../../Contexts/AuthContext';

const StyledSaveButton = styled(Button)`
    background: #2e69fe 0% 0% no-repeat padding-box !important;
    box-shadow: 0px 3px 3px #00000061;
    border-radius: 4px;
    opacity: 1;

    &.MuiButton-contained.Mui-disabled {
        color: #fafbf7;
    }
    :disabled {
        background: #9b9b9d 0% 0% no-repeat padding-box !important;
    }
`;
const UpdateEmailApplet = styled.div`
    margin-top: 40px;
    margin-left: auto;
    margin-right: auto;
    height: 600px;
    width: 700px;
    text-align: center;
    padding: 32px;
    background-color: #fafbf7;
    box-shadow: 2px 2px 2px #18131358;
    border-radius: 4px;
    opacity: 1;
`;

const UpdateEmailContainer = styled.div`
    display: flex;
    height: 900px;
    width: 800px;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
`;

const Label = styled.div`
    letter-spacing: 0px;
    color: #000000;
    text-align: left;
`;

const StyledValue = styled.span`
    letter-spacing: 0px;
    color: #000000;
    padding-left: 8px;
    font-weight: bold;
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    margin-top: 80px;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    width: 700px;
    margin-left: 50px;
    text-align: center;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledTextField = styled(TextField)`
    width: 250px;
`;
const UpdateEmail = () => {
    const { currentUser, verifyPassword, updateEmail } = useAuth();

    const [accountData, setAccountData] = useState({
        currentPassword: '',
        newEmail: '',
        confirmNewEmail: '',
        showCurrentPassword: false,
    });

    const initialPostDataValue = {
        message: '',
        input: '',
        data: null,
        isLoading: false,
    };
    const [postData, setPostData] = useState(initialPostDataValue);

    const { currentPassword, newEmail, confirmNewEmail, showCurrentPassword } = { ...accountData };

    const handleInputValidation = (error) => {
        if (error.code === 'auth/wrong-password') {
            setPostData({ message: 'Password is incorrect', input: 'currentPassword' });
        }
        if (error.code === 'auth/invalid-email') {
            setPostData({
                message: 'Email must be in correct format (test@test.com)',
                input: 'newEmail',
            });
        }
        if (error.code === 'auth/email-already-in-use') {
            setPostData({
                message: 'Email is already in use by another account.',
                input: 'newEmail',
            });
        }
        if (newEmail !== confirmNewEmail) {
            setPostData({ message: 'Emails do not match', input: 'confirmNewEmail' });
        }
    };
    const strongEmailRegex =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    const handleChange = (prop) => (event) => {
        setAccountData({ ...accountData, [prop]: event.target.value, [prop]: event.target.value });
    };

    const handleEmailValidation = () => {
        if (!newEmail.match(strongEmailRegex)) {
            setPostData({
                ...postData,
                message: 'Email must be in correct format (test@test.com)',
                input: 'newEmail',
            });
        } else if (confirmNewEmail === '') {
            setPostData({
                ...postData,
                message: 'Field is required',
                input: 'confirmNewEmail',
            });
        } else if (newEmail !== confirmNewEmail) {
            setPostData({
                ...postData,
                message: 'Emails do not match.',
                input: 'confirmNewEmail',
            });
        } else if (currentPassword === '') {
            setPostData({
                ...postData,
                message: 'Current password is required.',
                input: 'currentPassword',
            });
        } else if (currentPassword === newEmail) {
            setPostData({
                ...postData,
                message: 'Email can not be the same as existing email.',
                input: 'newEmail',
            });
        } else {
            setPostData({
                ...initialPostDataValue,
            });
        }
    };

    useEffect(() => {
        handleEmailValidation();
    }, [newEmail, confirmNewEmail, currentPassword]);

    const updateEmailFunc = async () => {
        try {
            setPostData({ ...postData, isLoading: true });
            await verifyPassword(currentPassword).then((resp) => {
                if (resp) {
                    // current email is correct, then change email
                    updateEmail(newEmail)
                        .then(() => {
                            setPostData({
                                ...postData,
                                data: 'Email Changed Succesfully',
                                isLoading: false,
                            });
                            window.location.reload();
                        })
                        .catch((error) => {
                            setPostData({ ...postData, data: null, isLoading: false });
                            handleInputValidation(error);
                        });
                }
            });
        } catch (error) {
            setPostData({ ...postData, data: null, isLoading: false });
            handleInputValidation(error);
        }
    };

    return (
        <UpdateEmailContainer>
            <Title>Change Email</Title>
            <UpdateEmailApplet>
                <Label>
                    Current Email:
                    <StyledValue>{currentUser.email}</StyledValue>
                </Label>
                <br />

                <InputContainer>
                    <Label>For your security, enter your current password:</Label>
                    <StyledTextField
                        id="current-password"
                        label="Current password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        defaultValue={currentPassword}
                        onChange={handleChange('currentPassword')}
                        variant="outlined"
                        error={postData.input === 'currentPassword'}
                        helperText={postData.input === 'currentPassword' ? postData.message : ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle email visibility"
                                        onClick={() =>
                                            setAccountData({
                                                ...accountData,
                                                showCurrentPassword: !showCurrentPassword,
                                            })
                                        }
                                        onMouseDown={() =>
                                            setAccountData({
                                                ...accountData,
                                                showCurrentPassword: !showCurrentPassword,
                                            })
                                        }
                                    >
                                        {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <br />
                    <Label>Update Email:</Label>

                    <StyledTextField
                        id="new-email"
                        label="New Email"
                        defaultValue={newEmail}
                        onChange={handleChange('newEmail')}
                        variant="outlined"
                        error={postData.input === 'newEmail'}
                        helperText={postData.input === 'newEmail' ? postData.message : ''}
                    />
                    <br />
                    <br />

                    <StyledTextField
                        id="confirm-new-email"
                        label="Confirm Email"
                        defaultValue={confirmNewEmail}
                        onChange={handleChange('confirmNewEmail')}
                        variant="outlined"
                        error={postData.input === 'confirmNewEmail'}
                        helperText={postData.input === 'confirmNewEmail' ? postData.message : ''}
                    />
                    <br />
                    <Label style={{ fontStyle: 'italic' }}>Email Criteria:</Label>
                    <br />
                    <Label style={{ fontStyle: 'italic', marginTop: '-23px' }}>
                        8-20 characters using 1 letter and 1 number.
                    </Label>
                </InputContainer>
                <br />
                <StyledSaveButton
                    disabled={!!(postData && postData.message)}
                    onClick={() => updateEmailFunc()}
                    variant="contained"
                    color="primary"
                >
                    {postData.isLoading ? (
                        <CircularProgress
                            style={{ height: '20px', width: '20px', color: 'white' }}
                            color="primary"
                        />
                    ) : (
                        'Save'
                    )}
                </StyledSaveButton>
                <div style={{ marginTop: '8px', color: 'green' }}>
                    {postData.data ? postData.data : null}
                </div>
            </UpdateEmailApplet>
        </UpdateEmailContainer>
    );
};

UpdateEmail.propTypes = {
    // companyData: propTypes.array,
    // companyDataError: propTypes.shape({}),
};

UpdateEmail.defaultProps = {
    // companyData: [],
    // companyDataError: {},
};

export default UpdateEmail;
